import type { PosFilterState } from '../components/posFilterTypes';

// ── View-model types shared with the Price tab chart ────────────────────────

export interface PriceByVintageSeriesPoint {
  vintage: string;
  minPrice: number | null;
  medianPrice: number | null;
  maxPrice: number | null;
  facing: number;
  posCount: number;
  consumptionRate?: number | null;
}

export interface PriceByVintageSeries {
  entityId: number;
  entityName: string;
  isOwnBrand: boolean;
  points: PriceByVintageSeriesPoint[];
}

export type PriceCompareMode = 'none' | 'previous' | 'competitor';

export const PRICE_COMPARE_OPTIONS: {
  value: PriceCompareMode;
  translationKey: string;
  disabled?: boolean;
}[] = [
  { value: 'none', translationKey: 'globalView.price.noComparison' },
  {
    value: 'previous',
    translationKey: 'globalView.price.previousYearComparison'
  },
  {
    value: 'competitor',
    translationKey: 'globalView.price.competitorComparison'
  }
];

// ── Query params for GET /v2/global-view/price-by-vintage ───────────────────

export interface PriceByVintageParams {
  network?: string;
  premium_store?: boolean;
  survey_year?: string;
  period?: 'latest';
  wineId: number | null;
  competitorIds: number[];
  competitorCustomIds?: number[];
  selectedClientCustomIds: number[];
  marketId: number[];
  continentId?: number | null;
  countryId?: number | null;
  wineTypeId?: number | null;
  wineColorId?: number | null;
  wineCountryId?: number | null;
  wineRegionId?: number | null;
  wineAppelationId?: number[] | null;
  wineGrapeVarietyId?: number[] | null;
  wineFormatGroupId?: number | null;
  wineFormatSize?: string[] | null;
  storeMenuPriceBandId?: number[] | null;
  storePrimarySubTypeId?: number[] | null;
  storeWsAward?: string[] | null;
  storeStar?: string[] | null;
  storeWorldsBest?: string[] | null;
  storeTopBar?: string[] | null;
  storeSpiritedAward?: string[] | null;
  vintage?: string[] | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  currency?: string | null;
}

export type VintageDistributionParams = PriceByVintageParams;

export interface VintageDistributionPoint {
  vintage: string;
  dn: number;
  facing: number;
  sharePct: number;
}

export interface VintageDistributionSeries {
  entityId: number;
  entityName: string;
  color: string | null;
  isOwnBrand: boolean;
  points: VintageDistributionPoint[];
}

export interface VintageDistributionIndicators {
  mostPresentVintage: string | null;
  ndOlderVintages: number | null;
  vintageSpreadYears: number | null;
}

export interface BuildPriceByVintageParamsArgs {
  gvFilters: PosFilterState;
  productTypeId: string | number | null;
  priceWine: string | number | null;
  priceCompare: PriceCompareMode;
  competitorIds: number[];
  competitorCustomIds?: number[];
  // Brand accounts pick a custom brand instead of a regular wine ref.
  useCustomBrands?: boolean;
}

export const buildPriceByVintageParams = ({
  gvFilters,
  productTypeId,
  priceWine,
  priceCompare,
  competitorIds,
  competitorCustomIds = [],
  useCustomBrands = false
}: BuildPriceByVintageParamsArgs): PriceByVintageParams => ({
  wineId: !useCustomBrands && priceWine != null ? Number(priceWine) : null,
  competitorIds: priceCompare === 'competitor' ? competitorIds : [],
  competitorCustomIds: priceCompare === 'competitor' ? competitorCustomIds : [],
  selectedClientCustomIds:
    useCustomBrands && priceWine != null
      ? [Number(priceWine)]
      : gvFilters.clientCustomRefs.map((r) => Number(r.value)),
  marketId: gvFilters.markets.map((m) => Number(m.value)),
  continentId:
    gvFilters.continentId !== 'all' ? Number(gvFilters.continentId) : null,
  countryId: gvFilters.countryId !== 'all' ? Number(gvFilters.countryId) : null,
  wineTypeId: productTypeId != null ? Number(productTypeId) : null,
  wineColorId:
    gvFilters.wineColor !== 'all' ? Number(gvFilters.wineColor) : null,
  wineCountryId:
    gvFilters.wineCountry !== 'all' ? Number(gvFilters.wineCountry) : null,
  wineRegionId:
    gvFilters.wineRegion !== 'all' ? Number(gvFilters.wineRegion) : null,
  wineAppelationId:
    gvFilters.wineAppelation !== 'all'
      ? [Number(gvFilters.wineAppelation)]
      : null,
  wineGrapeVarietyId:
    gvFilters.wineGrapeVariety.length > 0
      ? gvFilters.wineGrapeVariety.map((g) => Number(g.value))
      : null,
  // Backend defaults to the bottle format when this is omitted — never
  // hardcode a format id here.
  wineFormatSize:
    gvFilters.spiritFormatSize.length > 0
      ? gvFilters.spiritFormatSize.map((o) => String(o.value))
      : null,
  wineFormatGroupId:
    gvFilters.wineFormatGroup !== 'all'
      ? Number(gvFilters.wineFormatGroup)
      : null,
  storeMenuPriceBandId:
    gvFilters.storePriceBand.length > 0
      ? gvFilters.storePriceBand.map((p) => Number(p.value))
      : null,
  storePrimarySubTypeId:
    gvFilters.storePrimaryType.length > 0
      ? gvFilters.storePrimaryType.map((p) => Number(p.value))
      : null,
  storeWsAward:
    gvFilters.storeWsAward.length > 0
      ? gvFilters.storeWsAward.map((o) => String(o.value))
      : null,
  storeStar:
    gvFilters.storeMichelinStar.length > 0
      ? gvFilters.storeMichelinStar.map((o) => String(o.value))
      : null,
  storeWorldsBest:
    gvFilters.storeWorldsBest.length > 0
      ? gvFilters.storeWorldsBest.map((o) => String(o.value))
      : null,
  storeTopBar:
    gvFilters.storeTopBar.length > 0
      ? gvFilters.storeTopBar.map((o) => String(o.value))
      : null,
  storeSpiritedAward:
    gvFilters.storeSpiritedAward.length > 0
      ? gvFilters.storeSpiritedAward.map((o) => String(o.value))
      : null,
  vintage:
    gvFilters.vintage.length > 0
      ? gvFilters.vintage.map((v) => String(v.value))
      : null,
  minPrice: gvFilters.rangePrices?.[0] ?? null,
  maxPrice: gvFilters.rangePrices?.[1] ?? null
});

// ── Chart helpers ─────────────────────────────────────────────────────────

/** Sorts vintages ascending numerically, always placing "NV" (non-vintage) last. */
export const sortVintages = (vintages: string[]): string[] =>
  [...vintages].sort((a, b) => {
    if (a === 'NV' && b === 'NV') return 0;
    if (a === 'NV') return 1;
    if (b === 'NV') return -1;
    return Number(a) - Number(b);
  });

export const isVintageWithinRange = (
  vintage: string,
  from: string,
  to: string
): boolean => {
  if (from === 'all' && to === 'all') return true;
  const fromYear = from === 'all' ? -Infinity : Number(from);
  const toYear = to === 'all' ? Infinity : Number(to);
  const year = Number(vintage);
  return (
    Number.isFinite(year) &&
    year >= Math.min(fromYear, toYear) &&
    year <= Math.max(fromYear, toYear)
  );
};

export const getPreviousVintagePoint = <T>(
  vintage: string,
  pointsByVintage: ReadonlyMap<string, T>
): T | undefined => {
  const year = Number(vintage);
  return Number.isInteger(year)
    ? pointsByVintage.get(String(year - 1))
    : undefined;
};

/** Formats a price with the API-provided currency; never coerces null/undefined to "0". */
export const formatPrice = (
  price: number | null | undefined,
  currency: string
): string => {
  if (price == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(price);
};

/** True when at least one series has at least one point with a usable median price. */
export const hasPricedPoints = (series: PriceByVintageSeries[]): boolean =>
  series.some((s) => s.points.some((p) => p.medianPrice != null));
